import { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowLeft, Volume2, VolumeX, Check, Grid3X3, Lightbulb } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface CrossMathGameProps {
  onBack: () => void;
}

type DifficultyLevel = 'beginner' | 'intermediate' | 'advanced';
type GamePhase = 'playing' | 'checking' | 'result' | 'idle';

interface Cell {
  row: number;
  col: number;
  value: string;
  type: 'number' | 'operator' | 'equals' | 'empty' | 'editable';
  userValue: string;
  isCorrect: boolean | null;
  solution: number | null;
}

interface Puzzle {
  grid: Cell[][];
  editableCells: { row: number; col: number; solution: number }[];
}

const DIFFICULTY_CONFIG = {
  beginner: {
    hiddenCells: 2,
    operations: ['+', '-'],
    maxNumber: 15,
    label: 'Beginner',
    emoji: '🌱',
    description: '2 missing numbers, add & subtract',
    points: 10
  },
  intermediate: {
    hiddenCells: 3,
    operations: ['+', '-', '×'],
    maxNumber: 20,
    label: 'Intermediate',
    emoji: '⭐',
    description: '3 missing numbers, with multiply',
    points: 20
  },
  advanced: {
    hiddenCells: 4,
    operations: ['+', '-', '×'],
    maxNumber: 25,
    label: 'Advanced',
    emoji: '🚀',
    description: '4 missing numbers, harder math',
    points: 30
  }
};

export default function CrossMathGame({ onBack }: CrossMathGameProps) {
  const [difficulty, setDifficulty] = useState<DifficultyLevel>('beginner');
  const [duration, setDuration] = useState(3);
  const [isActive, setIsActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [weeklyStats, setWeeklyStats] = useState({ total_sessions: 0, total_minutes: 0 });
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [isClosingModal, setIsClosingModal] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);
  const [gamePhase, setGamePhase] = useState<GamePhase>('idle');
  const [roundResult, setRoundResult] = useState<'correct' | 'wrong' | null>(null);
  const [correctRounds, setCorrectRounds] = useState(0);
  const [totalRounds, setTotalRounds] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [hintsAvailable, setHintsAvailable] = useState(3);

  const audioContextRef = useRef<AudioContext | null>(null);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    loadStreak();
    loadWeeklyStats();
    
    const initAudio = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
      }
      document.removeEventListener('click', initAudio);
    };
    document.addEventListener('click', initAudio);
    return () => {
      document.removeEventListener('click', initAudio);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isActive && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            handleComplete();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isActive, timeRemaining]);

  const loadStreak = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase.rpc('get_activity_streak', {
        p_user_id: user.id,
        p_activity_type: 'mental_maths'
      });
      if (!error && data !== null) {
        setBestStreak(data);
      }
    } catch (error) {
      console.error('Error loading streak:', error);
    }
  };

  const loadWeeklyStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data, error } = await supabase.rpc('get_activity_weekly_stats', {
        p_user_id: user.id,
        p_activity_type: 'mental_maths'
      });
      if (!error && data && data.length > 0) {
        setWeeklyStats(data[0]);
      }
    } catch (error) {
      console.error('Error loading weekly stats:', error);
    }
  };

  const playSound = useCallback((type: 'tap' | 'correct' | 'wrong' | 'select') => {
    if (!soundEnabled || !audioContextRef.current) return;
    const ctx = audioContextRef.current;
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);

    switch (type) {
      case 'tap':
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(500, ctx.currentTime);
        gainNode.gain.setValueAtTime(0.1, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.08);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.08);
        break;
      case 'select':
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(600, ctx.currentTime);
        gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.1);
        break;
      case 'correct':
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(523, ctx.currentTime);
        oscillator.frequency.setValueAtTime(659, ctx.currentTime + 0.1);
        oscillator.frequency.setValueAtTime(784, ctx.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.2, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.3);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.3);
        break;
      case 'wrong':
        oscillator.type = 'sawtooth';
        oscillator.frequency.setValueAtTime(200, ctx.currentTime);
        oscillator.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.2);
        gainNode.gain.setValueAtTime(0.15, ctx.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2);
        oscillator.start(ctx.currentTime);
        oscillator.stop(ctx.currentTime + 0.2);
        break;
    }
  }, [soundEnabled]);

  const generatePuzzle = useCallback((): Puzzle => {
    const config = DIFFICULTY_CONFIG[difficulty];
    const ops = config.operations;
    const maxNum = config.maxNumber;

    // Generate valid equations
    const genEquation = (): { a: number; op: string; b: number; result: number } => {
      const op = ops[Math.floor(Math.random() * ops.length)];
      let a: number, b: number, result: number;

      if (op === '+') {
        a = Math.floor(Math.random() * (maxNum - 2)) + 2;
        b = Math.floor(Math.random() * (maxNum - 2)) + 2;
        result = a + b;
      } else if (op === '-') {
        a = Math.floor(Math.random() * (maxNum - 5)) + 8;
        b = Math.floor(Math.random() * (a - 2)) + 1;
        result = a - b;
      } else { // ×
        a = Math.floor(Math.random() * 8) + 2;
        b = Math.floor(Math.random() * 8) + 2;
        result = a * b;
      }

      return { a, op, b, result };
    };

    // Create equations
    const eq1 = genEquation(); // Row 0
    const eq2 = genEquation(); // Row 2

    // For vertical equations, we need to calculate what makes sense
    // Col 0: eq1.a [op1] X = result1
    // Col 2: eq1.b [op2] Y = result2
    
    // Simplified: just use addition for verticals
    const vertResult1 = eq1.a + eq2.a;
    const vertResult2 = eq1.b + eq2.b;

    // Initialize 5x5 grid
    const grid: Cell[][] = [];
    for (let i = 0; i < 5; i++) {
      grid[i] = [];
      for (let j = 0; j < 5; j++) {
        grid[i][j] = {
          row: i,
          col: j,
          value: '',
          type: 'empty',
          userValue: '',
          isCorrect: null,
          solution: null
        };
      }
    }

    // Row 0: a1 op1 b1 = r1
    grid[0][0] = { ...grid[0][0], value: String(eq1.a), type: 'number', solution: eq1.a };
    grid[0][1] = { ...grid[0][1], value: eq1.op === '×' ? '×' : eq1.op, type: 'operator' };
    grid[0][2] = { ...grid[0][2], value: String(eq1.b), type: 'number', solution: eq1.b };
    grid[0][3] = { ...grid[0][3], value: '=', type: 'equals' };
    grid[0][4] = { ...grid[0][4], value: String(eq1.result), type: 'number', solution: eq1.result };

    // Row 1: operators for vertical
    grid[1][0] = { ...grid[1][0], value: '+', type: 'operator' };
    grid[1][2] = { ...grid[1][2], value: '+', type: 'operator' };

    // Row 2: a2 op2 b2 = r2
    grid[2][0] = { ...grid[2][0], value: String(eq2.a), type: 'number', solution: eq2.a };
    grid[2][1] = { ...grid[2][1], value: eq2.op === '×' ? '×' : eq2.op, type: 'operator' };
    grid[2][2] = { ...grid[2][2], value: String(eq2.b), type: 'number', solution: eq2.b };
    grid[2][3] = { ...grid[2][3], value: '=', type: 'equals' };
    grid[2][4] = { ...grid[2][4], value: String(eq2.result), type: 'number', solution: eq2.result };

    // Row 3: equals for vertical
    grid[3][0] = { ...grid[3][0], value: '=', type: 'equals' };
    grid[3][2] = { ...grid[3][2], value: '=', type: 'equals' };

    // Row 4: vertical results
    grid[4][0] = { ...grid[4][0], value: String(vertResult1), type: 'number', solution: vertResult1 };
    grid[4][2] = { ...grid[4][2], value: String(vertResult2), type: 'number', solution: vertResult2 };

    // Collect all number cells that can be hidden
    const numberCells: { row: number; col: number; solution: number }[] = [
      { row: 0, col: 0, solution: eq1.a },
      { row: 0, col: 2, solution: eq1.b },
      { row: 2, col: 0, solution: eq2.a },
      { row: 2, col: 2, solution: eq2.b },
    ];

    // Shuffle and select cells to hide
    const shuffled = numberCells.sort(() => Math.random() - 0.5);
    const editableCells = shuffled.slice(0, config.hiddenCells);

    // Mark cells as editable
    editableCells.forEach(({ row, col }) => {
      grid[row][col] = {
        ...grid[row][col],
        type: 'editable',
        value: '',
        userValue: ''
      };
    });

    return { grid, editableCells };
  }, [difficulty]);

  const handleStart = () => {
    setIsActive(true);
    setTimeRemaining(duration * 60);
    setScore(0);
    setRound(1);
    setStreak(0);
    setCorrectRounds(0);
    setTotalRounds(0);
    setHintsUsed(0);
    setHintsAvailable(difficulty === 'beginner' ? 3 : difficulty === 'intermediate' ? 2 : 1);
    setSelectedCell(null);
    setGamePhase('playing');
    
    const newPuzzle = generatePuzzle();
    setPuzzle(newPuzzle);
  };

  const handleCellClick = (row: number, col: number) => {
    if (gamePhase !== 'playing' || !puzzle) return;
    const cell = puzzle.grid[row][col];
    if (cell.type !== 'editable') return;
    
    playSound('select');
    setSelectedCell({ row, col });
  };

  const handleNumberInput = (num: string) => {
    if (!puzzle || !selectedCell || gamePhase !== 'playing') return;
    
    playSound('tap');
    const { row, col } = selectedCell;
    const newGrid = puzzle.grid.map(r => r.map(c => ({ ...c })));
    const currentValue = newGrid[row][col].userValue;
    
    if (num === 'clear') {
      newGrid[row][col].userValue = '';
      newGrid[row][col].isCorrect = null;
    } else if (num === 'backspace') {
      newGrid[row][col].userValue = currentValue.slice(0, -1);
      newGrid[row][col].isCorrect = null;
    } else if (currentValue.length < 3) {
      newGrid[row][col].userValue = currentValue + num;
      newGrid[row][col].isCorrect = null;
    }
    
    setPuzzle({ ...puzzle, grid: newGrid });
  };

  const handleHint = () => {
    if (!puzzle || !selectedCell || hintsAvailable <= hintsUsed) return;
    
    const { row, col } = selectedCell;
    const cell = puzzle.grid[row][col];
    if (cell.type !== 'editable' || cell.solution === null) return;

    playSound('correct');
    const newGrid = puzzle.grid.map(r => r.map(c => ({ ...c })));
    newGrid[row][col].userValue = String(cell.solution);
    newGrid[row][col].isCorrect = true;
    
    setPuzzle({ ...puzzle, grid: newGrid });
    setHintsUsed(prev => prev + 1);
  };

  const handleSubmit = () => {
    if (!puzzle || gamePhase !== 'playing') return;

    setGamePhase('checking');
    setTotalRounds(prev => prev + 1);

    let allCorrect = true;
    const newGrid = puzzle.grid.map(r => r.map(c => ({ ...c })));

    puzzle.editableCells.forEach(({ row, col, solution }) => {
      const userVal = parseInt(newGrid[row][col].userValue);
      const isCorrect = userVal === solution;
      newGrid[row][col].isCorrect = isCorrect;
      if (!isCorrect) allCorrect = false;
    });

    setPuzzle({ ...puzzle, grid: newGrid });
    setRoundResult(allCorrect ? 'correct' : 'wrong');
    setGamePhase('result');

    if (allCorrect) {
      playSound('correct');
      const points = DIFFICULTY_CONFIG[difficulty].points;
      const streakBonus = streak * 5;
      setScore(prev => prev + points + streakBonus);
      setCorrectRounds(prev => prev + 1);
      setStreak(prev => {
        const newStreak = prev + 1;
        if (newStreak > bestStreak) setBestStreak(newStreak);
        return newStreak;
      });
    } else {
      playSound('wrong');
      setStreak(0);
    }

    // Show correct answers then move to next
    setTimeout(() => {
      if (timeRemaining > 0) {
        setRound(prev => prev + 1);
        setSelectedCell(null);
        setRoundResult(null);
        const newPuzzle = generatePuzzle();
        setPuzzle(newPuzzle);
        setGamePhase('playing');
      }
    }, 1500);
  };

  const handleComplete = async () => {
    setIsActive(false);
    setGamePhase('idle');
    setShowResultsModal(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const accuracyVal = totalRounds > 0 ? (correctRounds / totalRounds) * 100 : 0;

      await supabase.from('mind_body_activities').insert({
        user_id: user.id,
        activity_type: 'mental_maths',
        duration_minutes: duration,
        difficulty_level: difficulty,
        score: score,
        accuracy: accuracyVal,
        completed_at: new Date().toISOString()
      });

      await loadStreak();
      await loadWeeklyStats();
    } catch (error) {
      console.error('Error saving cross math session:', error);
    }
  };

  const handleCloseModal = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setShowResultsModal(false);
      setIsClosingModal(false);
      onBack();
    }, 300);
  };

  const handlePlayAgain = () => {
    setIsClosingModal(true);
    setTimeout(() => {
      setShowResultsModal(false);
      setIsClosingModal(false);
      handleStart();
    }, 300);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const accuracy = totalRounds > 0 ? Math.round((correctRounds / totalRounds) * 100) : 0;

  const renderCell = (cell: Cell) => {
    const isSelected = selectedCell?.row === cell.row && selectedCell?.col === cell.col;
    
    if (cell.type === 'empty') {
      return (
        <div
          key={`${cell.row}-${cell.col}`}
          className="aspect-square rounded-xl bg-slate-800/30"
        />
      );
    }

    if (cell.type === 'operator' || cell.type === 'equals') {
      return (
        <div
          key={`${cell.row}-${cell.col}`}
          className="aspect-square rounded-xl bg-slate-700/80 flex items-center justify-center text-teal-300 font-bold text-xl"
        >
          {cell.value}
        </div>
      );
    }

    if (cell.type === 'number') {
      return (
        <div
          key={`${cell.row}-${cell.col}`}
          className="aspect-square rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-slate-900 font-black text-lg shadow-lg"
        >
          {cell.value}
        </div>
      );
    }

    // Editable cell
    const displayValue = cell.userValue || '';
    let bgClass = 'bg-gradient-to-br from-teal-400/70 to-cyan-500/70';
    
    if (cell.isCorrect === true) {
      bgClass = 'bg-gradient-to-br from-green-400 to-emerald-500';
    } else if (cell.isCorrect === false) {
      bgClass = 'bg-gradient-to-br from-red-400 to-rose-500';
    } else if (isSelected) {
      bgClass = 'bg-gradient-to-br from-amber-300 to-orange-400 ring-2 ring-white ring-offset-2 ring-offset-slate-900';
    }

    return (
      <button
        key={`${cell.row}-${cell.col}`}
        onClick={() => handleCellClick(cell.row, cell.col)}
        disabled={gamePhase !== 'playing'}
        className={`aspect-square rounded-xl ${bgClass} flex items-center justify-center text-slate-900 font-black text-lg shadow-lg transition-all duration-200 ${
          gamePhase === 'playing' ? 'cursor-pointer hover:scale-105 active:scale-95' : 'cursor-default'
        }`}
      >
        {displayValue || '?'}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <header className="bg-white px-4 py-3 flex items-center justify-between sticky top-0 z-10 border-b border-gray-200">
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            <ArrowLeft size={18} className="text-gray-700" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-xl">🧩</span>
            <h1 className="text-base font-bold text-gray-900">Cross Math</h1>
          </div>
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className="w-9 h-9 rounded-xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
          >
            {soundEnabled ? (
              <Volume2 size={18} className="text-gray-700" />
            ) : (
              <VolumeX size={18} className="text-gray-700" />
            )}
          </button>
        </header>

        <div className="p-4 space-y-4">
          {!isActive ? (
            <>
              {/* Stats Row */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-2xl p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">🔥</span>
                    <p className="text-xs font-medium text-gray-500">Best Streak</p>
                  </div>
                  <p className="text-2xl font-black text-gray-900">{bestStreak} <span className="text-sm font-medium text-gray-400">puzzles</span></p>
                </div>

                <div className="bg-white rounded-2xl p-4 border border-gray-200">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-base">📊</span>
                    <p className="text-xs font-medium text-gray-500">This Week</p>
                  </div>
                  <p className="text-2xl font-black text-gray-900">{weeklyStats.total_sessions} <span className="text-sm font-medium text-gray-400">games</span></p>
                </div>
              </div>

              {/* Main Card */}
              <div className="bg-white rounded-2xl p-5 border border-gray-200">
                {/* Header */}
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-3 shadow-lg shadow-teal-500/20">
                    <Grid3X3 size={32} className="text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Cross Math</h2>
                  <p className="text-sm text-gray-500 mt-1">Solve the math crossword puzzle!</p>
                </div>

                {/* Duration Selection */}
                <div className="mb-4">
                  <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Duration</p>
                  <div className="grid grid-cols-4 gap-2">
                    {[1, 3, 5, 10].map((min) => (
                      <button
                        key={min}
                        onClick={() => setDuration(min)}
                        className={`py-2.5 rounded-xl font-bold text-sm transition-all active:scale-[0.95] ${
                          duration === min
                            ? 'bg-teal-600 text-white shadow-lg shadow-teal-500/20'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {min}m
                      </button>
                    ))}
                  </div>
                </div>

                {/* Difficulty Selection */}
                <div className="mb-5">
                  <p className="text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Difficulty</p>
                  <div className="space-y-2">
                    {(['beginner', 'intermediate', 'advanced'] as DifficultyLevel[]).map((level) => {
                      const config = DIFFICULTY_CONFIG[level];
                      return (
                        <button
                          key={level}
                          onClick={() => setDifficulty(level)}
                          className={`w-full p-3 rounded-xl flex items-center gap-3 transition-all active:scale-[0.99] border-2 ${
                            difficulty === level
                              ? 'border-teal-500 bg-teal-50'
                              : 'border-gray-200 bg-white hover:border-gray-300'
                          }`}
                        >
                          <span className="text-xl">{config.emoji}</span>
                          <div className="text-left flex-1">
                            <p className={`font-semibold text-sm ${difficulty === level ? 'text-teal-700' : 'text-gray-900'}`}>
                              {config.label}
                            </p>
                            <p className="text-xs text-gray-500">{config.description}</p>
                          </div>
                          {difficulty === level && (
                            <div className="w-5 h-5 bg-teal-600 rounded-full flex items-center justify-center">
                              <Check size={12} className="text-white" strokeWidth={3} />
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* How to Play */}
                <div className="bg-teal-50 rounded-xl p-4 mb-5 border border-teal-100">
                  <p className="text-xs font-bold text-teal-700 mb-2">💡 How to Play</p>
                  <ul className="text-xs text-gray-600 space-y-1">
                    <li>• Tap a ? cell to select it</li>
                    <li>• Enter the missing number using the keypad</li>
                    <li>• Equations must work horizontally AND vertically</li>
                    <li>• Submit to check your answers</li>
                  </ul>
                </div>

                {/* Start Button */}
                <button
                  onClick={handleStart}
                  className="w-full bg-gradient-to-r from-teal-500 to-cyan-600 text-white py-4 rounded-xl font-bold text-sm transition-all shadow-lg shadow-teal-500/20 active:scale-[0.98] flex items-center justify-center gap-2"
                >
                  <span className="text-lg">▶️</span>
                  Start Puzzle
                </button>
              </div>
            </>
          ) : (
            <>
              {/* Active Game Stats */}
              <div className="grid grid-cols-4 gap-2">
                <div className="bg-white rounded-xl p-2.5 border border-gray-200">
                  <p className="text-[10px] font-medium text-gray-500">Time</p>
                  <p className="text-base font-black text-gray-900">{formatTime(timeRemaining)}</p>
                </div>
                <div className="bg-white rounded-xl p-2.5 border border-gray-200">
                  <p className="text-[10px] font-medium text-gray-500">Score</p>
                  <p className="text-base font-black text-gray-900">{score}</p>
                </div>
                <div className="bg-white rounded-xl p-2.5 border border-gray-200">
                  <p className="text-[10px] font-medium text-gray-500">Puzzle</p>
                  <p className="text-base font-black text-gray-900">{round}</p>
                </div>
                <div className="bg-white rounded-xl p-2.5 border border-gray-200">
                  <p className="text-[10px] font-medium text-gray-500">Streak</p>
                  <p className="text-base font-black text-gray-900">{streak}🔥</p>
                </div>
              </div>

              {/* Game Phase Indicator */}
              <div className="text-center">
                {gamePhase === 'playing' && (
                  <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-bold border border-teal-200">
                    <Grid3X3 size={16} />
                    Fill in the missing numbers
                  </div>
                )}
                {gamePhase === 'result' && (
                  <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border ${
                    roundResult === 'correct' 
                      ? 'bg-green-100 text-green-700 border-green-200' 
                      : 'bg-red-100 text-red-700 border-red-200'
                  }`}>
                    {roundResult === 'correct' ? '✓ Correct! +' + DIFFICULTY_CONFIG[difficulty].points : '✗ Try the next one!'}
                  </div>
                )}
              </div>

              {/* 5x5 Puzzle Grid */}
              <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-4 border-4 border-teal-400/80 shadow-xl">
                <div className="grid grid-cols-5 gap-2">
                  {puzzle?.grid.map((row) => 
                    row.map((cell) => renderCell(cell))
                  )}
                </div>
              </div>

              {/* Hint Button */}
              <div className="flex justify-center">
                <button
                  onClick={handleHint}
                  disabled={!selectedCell || hintsUsed >= hintsAvailable || gamePhase !== 'playing'}
                  className="flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-xl font-semibold text-sm
                    disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:bg-amber-200 active:scale-95"
                >
                  <Lightbulb size={16} />
                  Hint ({hintsAvailable - hintsUsed} left)
                </button>
              </div>

              {/* Number Pad */}
              <div className="bg-white rounded-2xl p-4 border border-gray-200">
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <button
                      key={num}
                      onClick={() => handleNumberInput(String(num))}
                      disabled={!selectedCell || gamePhase !== 'playing'}
                      className="bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 text-xl font-bold py-4 rounded-xl border border-gray-200 transition-all active:scale-95"
                    >
                      {num}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {[6, 7, 8, 9, 0].map((num) => (
                    <button
                      key={num}
                      onClick={() => handleNumberInput(String(num))}
                      disabled={!selectedCell || gamePhase !== 'playing'}
                      className="bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-gray-900 text-xl font-bold py-4 rounded-xl border border-gray-200 transition-all active:scale-95"
                    >
                      {num}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => handleNumberInput('clear')}
                    disabled={!selectedCell || gamePhase !== 'playing'}
                    className="bg-red-50 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed text-red-600 py-3 rounded-xl font-bold text-sm transition-all active:scale-95 border border-red-100"
                  >
                    Clear
                  </button>
                  <button
                    onClick={() => handleNumberInput('backspace')}
                    disabled={!selectedCell || gamePhase !== 'playing'}
                    className="bg-amber-50 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed text-amber-600 py-3 rounded-xl font-bold text-sm transition-all active:scale-95 border border-amber-100"
                  >
                    ⌫
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={gamePhase !== 'playing'}
                    className="bg-gradient-to-r from-teal-500 to-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed text-white py-3 rounded-xl font-bold text-sm transition-all active:scale-95 shadow-lg shadow-teal-500/20"
                  >
                    Submit ✓
                  </button>
                </div>
              </div>

              {/* Instructions */}
              <div className="text-center text-gray-500 text-xs">
                Tap a ? cell, then use the keypad to enter the number
              </div>
            </>
          )}
        </div>
      </div>

      {/* Results Modal */}
      {showResultsModal && (
        <div
          className={`fixed inset-0 z-50 flex items-end justify-center transition-all duration-300 ${
            isClosingModal ? 'bg-black/0' : 'bg-black/50'
          }`}
          onClick={handleCloseModal}
        >
          <div
            className={`w-full max-w-lg bg-white rounded-t-[28px] transform transition-transform duration-300 ease-out ${
              isClosingModal ? 'translate-y-full' : 'translate-y-0'
            }`}
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-gray-300 rounded-full" />
            </div>

            <div className="px-5 pb-6">
              <div className="text-center mb-4">
                <div className="text-6xl mb-2">🧩</div>
                <h2 className="text-xl font-bold text-gray-900">Time's Up!</h2>
                <p className="text-sm text-gray-500">Great puzzle solving!</p>
              </div>

              <div className="grid grid-cols-3 gap-2 mb-4">
                <div className="bg-teal-50 border border-teal-200 rounded-2xl p-3 text-center">
                  <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center mx-auto mb-1">
                    <span className="text-lg">⭐</span>
                  </div>
                  <p className="text-xl font-black text-teal-600">{score}</p>
                  <p className="text-[10px] text-teal-600 font-medium">Score</p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-2xl p-3 text-center">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-1">
                    <span className="text-lg">✓</span>
                  </div>
                  <p className="text-xl font-black text-green-600">{correctRounds}</p>
                  <p className="text-[10px] text-green-600 font-medium">Solved</p>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-3 text-center">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-1">
                    <span className="text-lg">🔥</span>
                  </div>
                  <p className="text-xl font-black text-orange-600">{bestStreak}</p>
                  <p className="text-[10px] text-orange-600 font-medium">Best Streak</p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500">Accuracy</span>
                  <span className="text-sm font-bold text-gray-900">{accuracy}%</span>
                </div>
                <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-teal-500 to-cyan-600 transition-all duration-500"
                    style={{ width: `${accuracy}%` }}
                  />
                </div>
                <div className="flex justify-between mt-2 text-[10px] text-gray-500">
                  <span>{correctRounds} solved</span>
                  <span>{totalRounds - correctRounds} missed</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={handleCloseModal}
                  className="py-3.5 rounded-xl font-bold text-sm border-2 border-gray-200 text-gray-700 hover:bg-gray-50 transition-all active:scale-[0.98]"
                >
                  Done
                </button>
                <button
                  onClick={handlePlayAgain}
                  className="py-3.5 rounded-xl font-bold text-sm bg-gradient-to-r from-teal-500 to-cyan-600 text-white shadow-lg shadow-teal-500/20 transition-all active:scale-[0.98]"
                >
                  Play Again 🧩
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}